/**
 * Relay Protocol Integration
 * 
 * This module provides utilities for cross-chain bridging and swapping
 * using the Relay Protocol API (https://relay.link)
 * 
 * Ported from mavrk_bot Python implementation
 */

const RELAY_BASE = "https://api.relay.link";
const RELAY_WS = "wss://api.relay.link/ws";
const ZERO_ADDRESS = "0x0000000000000000000000000000000000000000";

// Relay Protocol provides chain logos via their Currencies API
// We'll use their logoURI for all chains
export const SUPPORTED_CHAINS = {
  ETHEREUM: {
    chainId: 1,
    name: "Ethereum",
    nativeCurrency: "ETH",
    logo: "https://icons.llamao.fi/icons/chains/rsz_ethereum.jpg",
    rpcUrl: "https://eth.llamarpc.com"
  },
  SEPOLIA: {
    chainId: 11155111,
    name: "Sepolia",
    nativeCurrency: "ETH",
    logo: "https://icons.llamao.fi/icons/chains/rsz_ethereum.jpg",
    rpcUrl: "https://sepolia.infura.io/v3/9aa3d95b3bc440fa88ea12eaa4456161"
  },
  BASE: {
    chainId: 8453,
    name: "Base Mainnet",
    nativeCurrency: "ETH",
    logo: "https://icons.llamao.fi/icons/chains/rsz_base.jpg",
    rpcUrl: "https://base-mainnet.infura.io"
  },
  OPTIMISM: {
    chainId: 10,
    name: "OP Mainnet",
    nativeCurrency: "ETH",
    logo: "https://icons.llamao.fi/icons/chains/rsz_optimism.jpg",
    rpcUrl: "https://optimism-mainnet.infura.io"
  },
  ARBITRUM: {
    chainId: 42161,
    name: "Arbitrum One",
    nativeCurrency: "ETH",
    logo: "https://icons.llamao.fi/icons/chains/rsz_arbitrum.jpg",
    rpcUrl: "https://arbitrum-mainnet.infura.io"
  },
  POLYGON: {
    chainId: 137,
    name: "Polygon",
    nativeCurrency: "POL",
    logo: "https://icons.llamao.fi/icons/chains/rsz_polygon.jpg",
    rpcUrl: "https://polygon-rpc.com"
  },
  BNB: {
    chainId: 56,
    name: "Binance Smart Chain",
    nativeCurrency: "BNB",
    logo: "https://icons.llamao.fi/icons/chains/rsz_binance.jpg",
    rpcUrl: "https://bsc-mainnet.infura.io"
  },
  AVALANCHE: {
    chainId: 43114,
    name: "Avalanche Network C-Chain",
    nativeCurrency: "AVAX",
    logo: "https://icons.llamao.fi/icons/chains/rsz_avalanche.jpg",
    rpcUrl: "https://avalanche-mainnet.infura.io"
  },
  INK: {
    chainId: 57073,
    name: "Ink",
    nativeCurrency: "ETH",
    logo: "https://icons.llamao.fi/icons/chains/rsz_ink.jpg",
    rpcUrl: "https://rpc-gel.inkonchain.com"
  },
  MONAD: {
    chainId: 143,
    name: "Monad",
    nativeCurrency: "MON",
    logo: "https://icons.llamao.fi/icons/chains/rsz_monad.jpg",
    rpcUrl: "https://rpc.monad.xyz"
  },
  SOLANA: {
    chainId: 101,
    name: "Solana",
    nativeCurrency: "SOL",
    logo: "https://icons.llamao.fi/icons/chains/rsz_solana.jpg",
    rpcUrl: "https://api.mainnet-beta.solana.com"
  }
} as const;

export type SupportedChain = typeof SUPPORTED_CHAINS[keyof typeof SUPPORTED_CHAINS];

// Relay quote request interface
export interface RelayQuoteRequest {
  user: string;
  originChainId: number;
  destinationChainId: number;
  originCurrency: string;
  destinationCurrency: string;
  amount: string; // Amount in wei
  tradeType: "EXACT_INPUT" | "EXACT_OUTPUT";
}

// Relay quote response interface
export interface RelayQuote {
  steps: Array<{
    requestId: string;
    items: Array<{
      data: {
        to: string;
        value: string;
        data: string;
        gas: number;
        chainId: number;
        gasPrice?: string;
        maxFeePerGas?: string;
        maxPriorityFeePerGas?: string;
      }
    }>
  }>;
  fees: {
    relayer: {
      amount: string;
      currency: string;
    };
    gas: {
      amount: string;
      currency: string;
    };
  };
  breakdown: {
    gross: string;
    net: string;
  };
}

// Relay status response interface
export interface RelayStatus {
  status: "pending" | "completed" | "success" | "failed";
  requestId: string;
  originTxHash?: string;
  destinationTxHash?: string;
  timestamp?: number;
  error?: string;
}

/**
 * Get a quote for bridging/swapping via Relay Protocol
 * 
 * @param fromChainId - Source chain ID
 * @param toChainId - Destination chain ID  
 * @param fromAddress - User's address
 * @param toAddress - Recipient address (usually same as fromAddress)
 * @param amountEth - Amount in ETH (will be converted to wei)
 * @param destinationTokenAddress - Optional token address on destination chain (for cross-chain swaps)
 * @returns Quote object with transaction data
 */
export async function getRelayQuote(
  fromChainId: number,
  toChainId: number,
  fromAddress: string,
  _toAddress: string,
  amountEth: number,
  destinationTokenAddress?: string
): Promise<RelayQuote> {
  // Convert ETH to wei with high precision
  const amountWei = BigInt(Math.floor(amountEth * 1e18)).toString();
  
  // Let Relay handle minimum amount validation
  // The official inkonchain.com/bridge supports smaller amounts
  
  console.log(`[Relay] Converting ${amountEth} ETH to ${amountWei} wei`);
  
  // If destinationTokenAddress is provided, use it; otherwise use native currency (ZERO_ADDRESS)
  const destinationCurrency = destinationTokenAddress || ZERO_ADDRESS;
  
  console.log(`[Relay] Destination currency: ${destinationCurrency === ZERO_ADDRESS ? 'Native ETH' : destinationTokenAddress}`);
  
  const payload: RelayQuoteRequest = {
    user: fromAddress,
    originChainId: fromChainId,
    destinationChainId: toChainId,
    originCurrency: ZERO_ADDRESS, // Native currency
    destinationCurrency: destinationCurrency, // Native or token address
    amount: amountWei,
    tradeType: "EXACT_INPUT"
  };
  
  const url = `${RELAY_BASE}/quote`;
  console.log(`[Relay] POST ${url}`, payload);
  
  try {
    const response = await fetch(url, {
      method: 'POST',
      headers: { 
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      body: JSON.stringify(payload)
    });
    
    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Relay quote failed (${response.status}): ${errorText}`);
    }
    
    const data = await response.json();
    console.log('[Relay] Quote response:', data);
    return data;
  } catch (error) {
    console.error('[Relay] Quote error:', error);
    if (error instanceof Error) {
      throw error;
    }
    throw new Error('Failed to get Relay quote');
  }
}

/**
 * Extract origin transaction data from quote response
 */
export function extractOriginTx(quote: RelayQuote) {
  try {
    const steps = quote.steps || [];
    if (steps.length === 0) {
      throw new Error("No steps in quote response");
    }
    
    const depositStep = steps[0];
    const items = depositStep.items || [];
    if (items.length === 0) {
      throw new Error("No items in deposit step");
    }
    
    const txData = items[0].data;
    if (!txData) {
      throw new Error("No transaction data in deposit step item");
    }
    
    return txData;
  } catch (error) {
    throw new Error(`Failed to extract origin tx from quote: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }
}

/**
 * Extract request ID from quote response
 */
export function extractRequestId(quote: RelayQuote): string {
  try {
    const steps = quote.steps || [];
    if (steps.length === 0) {
      throw new Error("No steps in quote response");
    }
    
    const depositStep = steps[0];
    const requestId = depositStep.requestId;
    if (!requestId) {
      throw new Error("No requestId in deposit step");
    }
    
    return requestId;
  } catch (error) {
    throw new Error(`Failed to extract requestId from quote: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }
}

/**
 * Get the status of a bridge transaction
 * 
 * @param requestId - The request ID from the quote
 * @returns Status object
 */
export async function getIntentStatus(requestId: string): Promise<RelayStatus> {
  const url = `${RELAY_BASE}/intents/status`;
  const params = new URLSearchParams({ requestId });
  
  console.log(`[Relay] GET ${url}?${params}`);
  
  try {
    const response = await fetch(`${url}?${params}`, {
      method: 'GET',
      headers: { 'Accept': 'application/json' }
    });
    
    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Relay status failed (${response.status}): ${errorText}`);
    }
    
    const data = await response.json();
    return data;
  } catch (error) {
    console.error('[Relay] Status error:', error);
    if (error instanceof Error) {
      throw error;
    }
    throw new Error('Failed to get bridge status');
  }
}

/**
 * Create a WebSocket connection to stream bridge status updates
 * 
 * @param requestId - The request ID from the quote
 * @param onEvent - Callback for status events
 * @returns WebSocket instance
 */
export function streamStatus(
  requestId: string,
  onEvent: (event: any) => void
): WebSocket {
  const ws_url = `${RELAY_WS}?requestId=${requestId}`;
  console.log(`[Relay] Opening WebSocket: ${ws_url}`);
  
  const ws = new WebSocket(ws_url);
  
  ws.onopen = () => {
    console.log('[Relay] WebSocket connected');
  };
  
  ws.onmessage = (event) => {
    try {
      const data = JSON.parse(event.data);
      console.log('[Relay] WebSocket message:', data);
      onEvent(data);
    } catch (error) {
      console.warn('[Relay] Failed to parse WebSocket message:', event.data);
      onEvent({ raw: event.data });
    }
  };
  
  ws.onerror = (error) => {
    console.error('[Relay] WebSocket error:', error);
  };
  
  ws.onclose = () => {
    console.log('[Relay] WebSocket closed');
  };
  
  return ws;
}

/**
 * Poll for bridge completion (alternative to WebSocket)
 * 
 * @param requestId - The request ID from the quote
 * @param maxAttempts - Maximum number of polling attempts
 * @param intervalMs - Interval between attempts in milliseconds
 * @returns Promise that resolves when bridge is complete
 */
export async function waitForBridgeCompletion(
  requestId: string,
  maxAttempts: number = 30,
  intervalMs: number = 2000
): Promise<RelayStatus> {
  console.log(`[Relay] Waiting for bridge completion: ${requestId}`);
  
  for (let attempt = 0; attempt < maxAttempts; attempt++) {
    try {
      const status = await getIntentStatus(requestId);
      
      if (status.status === "completed" || status.status === "success") {
        console.log(`[Relay] Bridge completed after ${attempt + 1} attempts`);
        return status;
      }
      
      if (status.status === "failed") {
        throw new Error(`Bridge failed: ${status.error || 'Unknown error'}`);
      }
      
      // Still pending, wait and try again
      console.log(`[Relay] Attempt ${attempt + 1}/${maxAttempts}: status = ${status.status}`);
      await new Promise(resolve => setTimeout(resolve, intervalMs));
      
    } catch (error) {
      console.error(`[Relay] Error checking status:`, error);
      
      // If this is the last attempt, throw the error
      if (attempt === maxAttempts - 1) {
        throw error;
      }
      
      // Otherwise, wait and retry
      await new Promise(resolve => setTimeout(resolve, intervalMs));
    }
  }
  
  throw new Error(`Bridge timeout after ${maxAttempts * intervalMs / 1000} seconds`);
}

/**
 * Calculate estimated fees for a cross-chain swap
 * 
 * @param quote - Quote response from Relay
 * @returns Object with fee breakdown
 */
export function calculateFees(quote: RelayQuote) {
  const relayerFee = quote.fees?.relayer?.amount || "0";
  const gasFee = quote.fees?.gas?.amount || "0";
  const gross = quote.breakdown?.gross || "0";
  const net = quote.breakdown?.net || "0";
  
  // Convert from wei to ETH for display
  const relayerFeeEth = Number(relayerFee) / 1e18;
  const gasFeeEth = Number(gasFee) / 1e18;
  const grossEth = Number(gross) / 1e18;
  const netEth = Number(net) / 1e18;
  const totalFeeEth = grossEth - netEth;
  
  return {
    relayerFee: relayerFeeEth,
    gasFee: gasFeeEth,
    totalFee: totalFeeEth,
    gross: grossEth,
    net: netEth,
    feePercentage: grossEth > 0 ? (totalFeeEth / grossEth) * 100 : 0
  };
}

/**
 * Get chain configuration by chain ID
 */
export function getChainById(chainId: number): SupportedChain | undefined {
  return Object.values(SUPPORTED_CHAINS).find(chain => chain.chainId === chainId);
}

/**
 * Get chain configuration by name
 */
export function getChainByName(name: string): SupportedChain | undefined {
  return Object.values(SUPPORTED_CHAINS).find(
    chain => chain.name.toLowerCase() === name.toLowerCase()
  );
}

/**
 * Currency response from Relay API
 */
export interface RelayCurrency {
  chainId: number;
  address: string;
  symbol: string;
  name: string;
  decimals: number;
  vmType: string;
  metadata: {
    logoURI?: string;
    verified?: boolean;
    isNative?: boolean;
  };
}

/**
 * Fetch supported currencies from Relay Protocol
 * This can be used to dynamically get chain logos and supported currencies
 * 
 * @param chainIds - Optional array of chain IDs to filter
 * @returns Array of currency objects with logos
 */
export async function getRelayCurrencies(chainIds?: number[]): Promise<RelayCurrency[]> {
  const url = `${RELAY_BASE}/currencies/v2`;
  
  const payload = {
    defaultList: true,
    verified: true,
    ...(chainIds && chainIds.length > 0 && { chainIds })
  };
  
  console.log(`[Relay] POST ${url}`, payload);
  
  try {
    const response = await fetch(url, {
      method: 'POST',
      headers: { 
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      body: JSON.stringify(payload)
    });
    
    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Relay currencies failed (${response.status}): ${errorText}`);
    }
    
    const data = await response.json();
    console.log('[Relay] Currencies response:', data);
    return data;
  } catch (error) {
    console.error('[Relay] Currencies error:', error);
    if (error instanceof Error) {
      throw error;
    }
    throw new Error('Failed to get Relay currencies');
  }
}

/**
 * Get chain logo URL from Relay API
 * Falls back to default if not found
 * 
 * @param chainId - Chain ID to get logo for
 * @returns Logo URL
 */
export async function getChainLogoFromRelay(chainId: number): Promise<string> {
  try {
    const currencies = await getRelayCurrencies([chainId]);
    
    // Find the native currency for this chain
    const nativeCurrency = currencies.find(
      currency => currency.chainId === chainId && currency.metadata?.isNative
    );
    
    if (nativeCurrency?.metadata?.logoURI) {
      return nativeCurrency.metadata.logoURI;
    }
    
    // Fallback to first currency with a logo
    const firstWithLogo = currencies.find(currency => currency.metadata?.logoURI);
    return firstWithLogo?.metadata?.logoURI || '';
  } catch (error) {
    console.error(`Failed to get logo for chain ${chainId}:`, error);
    return '';
  }
}

